import { supabase } from '../lib/supabase';

export interface MatchTimelineEvent {
  id: string;
  match_id: string;
  team_id?: string;
  player_id?: string;
  stat_type_id: string;
  action: 'increment' | 'decrement';
  previous_value: number;
  new_value: number;
  match_minute: number;
  timestamp: string;
  user_id: string;
  created_at: string;
}

export interface MatchStatus {
  status: 'not_started' | 'live' | 'half_time' | 'full_time' | 'editing' | 'extra_time_1st_half' | 'extra_time_half_time' | 'extra_time_2nd_half' | 'penalty_shootout';
  current_minute: number;
  kick_off_minute?: number;
  end_1st_half_minute?: number;
  start_2nd_half_minute?: number;
  start_extra_time_1st_half_minute?: number;
  end_extra_time_1st_half_minute?: number;
  start_extra_time_2nd_half_minute?: number;
  penalty_shootout_minute?: number;
  full_time_minute?: number;
}

export class MatchTimelineService {
  /**
   * Record a match timeline event (kick_off, end_1st_half, start_2nd_half, full_time, substitution, extra_time events, penalty_shootout)
   */
  static async recordMatchEvent(
    matchId: string,
    eventType: 'kick_off' | 'end_1st_half' | 'start_2nd_half' | 'full_time' | 'substitution' | 'start_extra_time_1st_half' | 'end_extra_time_1st_half' | 'start_extra_time_2nd_half' | 'penalty_shootout',
    minute: number,
    teamId?: string,
    playerId?: string,
    videoTimeSeconds?: number | null,
    videoId?: string | null
  ): Promise<string | null> {
    try {
      // Get the stat type ID using cached lookup (performance optimization)
      const { scoresheetService } = await import('./scoresheetService');
      const statTypeId = await scoresheetService.getStatTypeId(eventType);

      // Calculate match minute from video timing if provided
      let calculatedMatchMinute: number | null = null;
      let logVideoTimeSeconds: number | null = null;
      let logVideoId: string | null = null;

      // Check if this is a break event - for break events, we use the provided minute
      // instead of calculating from existing timing points
      const breakEvents = ['start_2nd_half', 'start_extra_time_1st_half', 'start_extra_time_2nd_half'];
      const isBreakEvent = breakEvents.includes(eventType);

      if (videoId && videoTimeSeconds !== null && videoTimeSeconds !== undefined) {
        try {
          // Get video's timing points
          const { matchVideosService } = await import('./matchVideosService');
          const timingPointsResult = await matchVideosService.getTimingPoints(videoId);
          
          if (timingPointsResult.success && timingPointsResult.data) {
            logVideoTimeSeconds = videoTimeSeconds;
            logVideoId = videoId;

            // For break events, add timing point using the provided minute (user-entered)
            // For other events, calculate match minute from existing timing points
            if (isBreakEvent) {
              // Use the provided minute for break events (user entered in modal)
              await matchVideosService.addTimingPoint(
                videoId,
                minute, // Use provided minute (user-entered match minute)
                videoTimeSeconds,
                eventType,
                undefined // sourceLogId will be set after log is created
              );
              // For break events, the match minute is the provided minute
              calculatedMatchMinute = minute;
            } else {
              // For non-break events, calculate from existing timing points
              const { calculateMatchMinute } = await import('../utils/videoTimingUtils');
              calculatedMatchMinute = calculateMatchMinute(videoTimeSeconds, timingPointsResult.data);
            }
          }
        } catch (error) {
          console.warn('Error calculating match minute from video timing:', error);
          // Continue without video timing - don't break the event recording
        }
      }

      // Use calculated match minute if available, otherwise use provided minute
      const finalMatchMinute = calculatedMatchMinute !== null ? calculatedMatchMinute : minute;

      // Get user ID for log entry
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || 'system';

      // Prepare operations that can run in parallel
      const isGameFlowEvent = ['kick_off', 'end_1st_half', 'start_2nd_half', 'full_time', 'substitution', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout'].includes(eventType);

      // Parallelize independent database operations: insert log and upsert scoresheet
      const [logResult, scoresheetResult] = await Promise.allSettled([
        // Insert scoresheet_logs
        supabase
          .from('scoresheet_logs')
          .insert({
            match_id: matchId,
            team_id: teamId,
            player_id: playerId,
            stat_type_id: statTypeId,
            action: 'increment',
            previous_value: 0,
            new_value: minute,
            match_minute: finalMatchMinute,
            video_time_seconds: logVideoTimeSeconds,
            video_id: logVideoId,
            timestamp: new Date().toISOString(),
            user_id: userId,
            is_active: true
          })
          .select('id')
          .single(),
        
        // Upsert scoresheet (only for game flow events, run in parallel)
        isGameFlowEvent
          ? supabase
              .from('scoresheet')
              .upsert({
                match_id: matchId,
                team_id: teamId || null,
                stat_type_id: statTypeId,
                value: minute,
                updated_at: new Date().toISOString()
              })
          : Promise.resolve({ data: null, error: null })
      ]);

      // Handle log insertion result
      if (logResult.status === 'rejected' || (logResult.status === 'fulfilled' && logResult.value.error)) {
        const error = logResult.status === 'rejected' ? logResult.reason : logResult.value.error;
        throw new Error(`Failed to record ${eventType}: ${error?.message || 'Unknown error'}`);
      }

      const logId = logResult.status === 'fulfilled' && logResult.value.data?.id || null;

      // Handle scoresheet upsert result
      if (isGameFlowEvent && scoresheetResult.status === 'rejected') {
        throw new Error(`Failed to update scoresheet for ${eventType}: ${scoresheetResult.reason?.message || 'Unknown error'}`);
      }
      if (isGameFlowEvent && scoresheetResult.status === 'fulfilled' && scoresheetResult.value.error) {
        throw new Error(`Failed to update scoresheet for ${eventType}: ${scoresheetResult.value.error.message}`);
      }

      // If this was a break event and we have a log ID, update the timing point with source_log_id
      if (logId && videoId && videoTimeSeconds !== null && videoTimeSeconds !== undefined && isBreakEvent) {
        try {
          const { matchVideosService } = await import('./matchVideosService');
          // Update the timing point with the log ID (use provided minute, not calculated)
          await matchVideosService.addTimingPoint(
            videoId,
            minute, // Use provided minute (user-entered match minute)
            videoTimeSeconds,
            eventType,
            logId
          );
        } catch (error) {
          console.warn('Error updating timing point with log ID:', error);
          // Non-critical - timing point was already added
        }
      }

      return logId;
    } catch (error) {
      console.error(`Error recording ${eventType}:`, error);
      throw error;
    }
  }

  /**
   * Get current match status and timeline
   */
  static async getMatchStatus(matchId: string): Promise<MatchStatus> {
    try {
      // First check the matches table for current status
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('status')
        .eq('id', matchId)
        .single();

      if (matchError) {
        console.error('Error fetching match status from matches table:', matchError);
      }

      // If match status is 'editing', return it directly
      if (matchData?.status === 'editing') {
        console.log('🎯 Match is in editing mode, returning editing status');
        return {
          status: 'editing',
          current_minute: 0,
          kick_off_minute: undefined,
          end_1st_half_minute: undefined,
          start_2nd_half_minute: undefined,
          start_extra_time_1st_half_minute: undefined,
          end_extra_time_1st_half_minute: undefined,
          start_extra_time_2nd_half_minute: undefined,
          penalty_shootout_minute: undefined,
          full_time_minute: undefined
        };
      }

      console.log('🔍 Match status from DB:', matchData?.status);

      // Get all game flow events for this match from scoresheet_logs
      const { data: events, error } = await supabase
        .from('scoresheet_logs')
        .select(`
          stat_type_id,
          new_value,
          stat_types!inner(stat_key)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .in('stat_types.stat_key', ['kick_off', 'end_1st_half', 'start_2nd_half', 'full_time', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout']);

      if (error) {
        throw new Error(`Failed to get match status: ${error.message}`);
      }

      const kickOff = events?.find(e => (e.stat_types as any)?.stat_key === 'kick_off');
      const end1stHalf = events?.find(e => (e.stat_types as any)?.stat_key === 'end_1st_half');
      const start2ndHalf = events?.find(e => (e.stat_types as any)?.stat_key === 'start_2nd_half');
      const startExtraTime1stHalf = events?.find(e => (e.stat_types as any)?.stat_key === 'start_extra_time_1st_half');
      const endExtraTime1stHalf = events?.find(e => (e.stat_types as any)?.stat_key === 'end_extra_time_1st_half');
      const startExtraTime2ndHalf = events?.find(e => (e.stat_types as any)?.stat_key === 'start_extra_time_2nd_half');
      const penaltyShootout = events?.find(e => (e.stat_types as any)?.stat_key === 'penalty_shootout');
      const fullTime = events?.find(e => (e.stat_types as any)?.stat_key === 'full_time');

      // Determine current status
      let status: MatchStatus['status'] = 'not_started';
      let currentMinute = 0;

      if (fullTime) {
        status = 'full_time';
        currentMinute = fullTime.new_value;
      } else if (penaltyShootout) {
        status = 'penalty_shootout';
        currentMinute = penaltyShootout.new_value;
      } else if (startExtraTime2ndHalf) {
        status = 'extra_time_2nd_half';
        currentMinute = startExtraTime2ndHalf.new_value;
      } else if (endExtraTime1stHalf && !startExtraTime2ndHalf) {
        status = 'extra_time_half_time';
        currentMinute = endExtraTime1stHalf.new_value;
      } else if (startExtraTime1stHalf) {
        status = 'extra_time_1st_half';
        currentMinute = startExtraTime1stHalf.new_value;
      } else if (end1stHalf && !start2ndHalf) {
        status = 'half_time';
        currentMinute = end1stHalf.new_value;
      } else if (start2ndHalf) {
        status = 'live';
        // Second half is active - use the start_2nd_half minute as base
        currentMinute = start2ndHalf.new_value;
      } else if (kickOff) {
        status = 'live';
        // First half is active - use kick_off minute + 1 as current minute
        currentMinute = kickOff.new_value || 1;
      }

      return {
        status,
        current_minute: currentMinute,
        kick_off_minute: kickOff?.new_value,
        end_1st_half_minute: end1stHalf?.new_value,
        start_2nd_half_minute: start2ndHalf?.new_value,
        start_extra_time_1st_half_minute: startExtraTime1stHalf?.new_value,
        end_extra_time_1st_half_minute: endExtraTime1stHalf?.new_value,
        start_extra_time_2nd_half_minute: startExtraTime2ndHalf?.new_value,
        penalty_shootout_minute: penaltyShootout?.new_value,
        full_time_minute: fullTime?.new_value
      };
    } catch (error) {
      console.error('Error getting match status:', error);
      return {
        status: 'not_started',
        current_minute: 0,
        kick_off_minute: undefined,
        end_1st_half_minute: undefined,
        start_2nd_half_minute: undefined,
        start_extra_time_1st_half_minute: undefined,
        end_extra_time_1st_half_minute: undefined,
        start_extra_time_2nd_half_minute: undefined,
        penalty_shootout_minute: undefined,
        full_time_minute: undefined
      };
    }
  }

  /**
   * Reset match - clear all data
   * 
   * Deletion order matters due to foreign key constraints:
   * 1. Delete child tables first (match_visualization, match_timeline, key_match_events)
   * 2. Then delete parent tables (scoresheet_logs, scoresheet)
   * 3. Finally update tables (match_lineup, matches)
   */
  static async resetMatch(matchId: string): Promise<void> {
    try {
      // 1. Delete all match visualization events FIRST (has FK to scoresheet_logs)
      const { error: visualizationError } = await supabase
        .from('match_visualization')
        .delete()
        .eq('match_id', matchId);

      if (visualizationError) {
        throw new Error(`Failed to delete match visualization: ${visualizationError.message}`);
      }

      // 2. Delete all match timeline events (has FK to scoresheet_logs)
      const { error: timelineError } = await supabase
        .from('match_timeline')
        .delete()
        .eq('match_id', matchId);

      if (timelineError) {
        throw new Error(`Failed to delete match timeline: ${timelineError.message}`);
      }

      // 3. Delete all key match events (has FK to scoresheet_logs)
      const { error: keyEventsError } = await supabase
        .from('key_match_events')
        .delete()
        .eq('match_id', matchId);

      if (keyEventsError) {
        throw new Error(`Failed to delete key match events: ${keyEventsError.message}`);
      }

      // 4. Delete all match video tags
      const { error: videoTagsError } = await supabase
        .from('match_video_tags')
        .delete()
        .eq('match_id', matchId);

      if (videoTagsError) {
        throw new Error(`Failed to delete match video tags: ${videoTagsError.message}`);
      }

      // 5. Delete all scoresheet logs FIRST (has FK to match_videos via video_id)
      // Must delete before match_videos to avoid foreign key constraint violation
      const { error: logsError } = await supabase
        .from('scoresheet_logs')
        .delete()
        .eq('match_id', matchId);

      if (logsError) {
        throw new Error(`Failed to delete logs: ${logsError.message}`);
      }

      // 6. Delete all match videos (after scoresheet_logs that reference them)
      const { error: videosError } = await supabase
        .from('match_videos')
        .delete()
        .eq('match_id', matchId);

      if (videosError) {
        throw new Error(`Failed to delete match videos: ${videosError.message}`);
      }

      // 7. Clear all scoresheet values
      const { error: scoresheetError } = await supabase
        .from('scoresheet')
        .delete()
        .eq('match_id', matchId);

      if (scoresheetError) {
        throw new Error(`Failed to clear scoresheet: ${scoresheetError.message}`);
      }

      // 8. Delete all player-level scoresheet rows (player_scoresheet)
      const { error: playerScoresheetError } = await supabase
        .from('player_scoresheet')
        .delete()
        .eq('match_id', matchId);

      if (playerScoresheetError) {
        throw new Error(`Failed to clear player scoresheet: ${playerScoresheetError.message}`);
      }

      // Clear substitution data (play_start_time, play_stop_time)
      const { error: lineupError } = await supabase
        .from('match_lineup')
        .update({
          play_start_time: null,
          play_stop_time: null
        })
        .eq('match_id', matchId);

      if (lineupError) {
        throw new Error(`Failed to clear substitutions: ${lineupError.message}`);
      }

      // Reset match scores to 0-0 and penalty scores to 0-0
      const { error: matchError } = await supabase
        .from('matches')
        .update({
          home_score: 0,
          away_score: 0,
          home_penalty_score: 0,
          away_penalty_score: 0,
          status: 'upcoming'
        })
        .eq('id', matchId);

      if (matchError) {
        throw new Error(`Failed to reset match scores: ${matchError.message}`);
      }
    } catch (error) {
      console.error('Error resetting match:', error);
      throw error;
    }
  }

  /**
   * Check if match is live (between kick_off and full_time)
   */
  static async isMatchLive(matchId: string): Promise<boolean> {
    const status = await this.getMatchStatus(matchId);
    console.log('🔍 isMatchLive check for match:', matchId, 'status:', status.status);
    const isLive = status.status === 'live' || 
           status.status === 'half_time' || 
           status.status === 'editing' ||
           status.status === 'extra_time_1st_half' || 
           status.status === 'extra_time_half_time' || 
           status.status === 'extra_time_2nd_half' || 
           status.status === 'penalty_shootout';
    console.log('🔍 isMatchLive result:', isLive);
    return isLive;
  }

  /**
   * Record substitution event
   */
  static async recordSubstitution(
    matchId: string,
    teamId: string,
    playerInId: string,
    playerOutId: string,
    minute?: number
  ): Promise<void> {
    try {
      console.log(`Recording substitution: ${playerOutId} out, ${playerInId} in at minute ${minute}`);

      // Get substitution stat types
      const { data: subInStatType, error: subInError } = await supabase
        .from('stat_types')
        .select('id')
        .eq('stat_key', 'substitution_in')
        .single();

      const { data: subOutStatType, error: subOutError } = await supabase
        .from('stat_types')
        .select('id')
        .eq('stat_key', 'substitution_out')
        .single();

      if (subInError || !subInStatType || subOutError || !subOutStatType) {
        console.error('Error fetching substitution stat types:', subInError?.message || subOutError?.message);
        throw new Error('Substitution stat types not found');
      }

      const currentMinute = minute || 1; // Default to minute 1 if not provided

      // Record player out
      const { error: logOutError } = await supabase.from('scoresheet_logs').insert({
        match_id: matchId,
        team_id: teamId,
        stat_type_id: subOutStatType.id,
        action: 'increment',
        previous_value: 0,
        new_value: 1, // Represents one substitution event
        match_minute: currentMinute,
        user_id: null, // Will be filled by auth context later
        is_active: true,
        timestamp: new Date().toISOString(),
        player_id: playerOutId, // Associate with player
      });

      if (logOutError) {
        console.error('Error logging substitution out:', logOutError);
        throw logOutError;
      }

      // Record player in
      const { error: logInError } = await supabase.from('scoresheet_logs').insert({
        match_id: matchId,
        team_id: teamId,
        stat_type_id: subInStatType.id,
        action: 'increment',
        previous_value: 0,
        new_value: 1, // Represents one substitution event
        match_minute: currentMinute,
        user_id: null, // Will be filled by auth context later
        is_active: true,
        timestamp: new Date().toISOString(),
        player_id: playerInId, // Associate with player
      });

      if (logInError) {
        console.error('Error logging substitution in:', logInError);
        throw logInError;
      }

      console.log(`Substitution for match ${matchId} logged successfully.`);
    } catch (error) {
      console.error('Error recording substitution:', error);
      throw error;
    }
  }

  /**
   * Get match timeline events
   */
  static async getMatchTimeline(matchId: string): Promise<MatchTimelineEvent[]> {
    try {
      const { data, error } = await supabase
        .from('scoresheet_logs')
        .select(`
          *,
          stat_types!inner(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .in('stat_types.stat_key', ['kick_off', 'half_time', 'full_time', 'substitution_in', 'substitution_out'])
        .order('match_minute', { ascending: true });

      if (error) {
        throw new Error(`Failed to get match timeline: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error getting match timeline:', error);
      return [];
    }
  }

  /**
   * Enable editing mode for a full-time match
   */
  static async enableEditingMode(matchId: string): Promise<void> {
    try {
      console.log('🔄 Enabling editing mode for match:', matchId);
      
      // Update match status to editing in the matches table
      const { error } = await supabase
        .from('matches')
        .update({ status: 'editing' })
        .eq('id', matchId);

      if (error) {
        console.error('Error enabling editing mode:', error);
        throw error;
      }

      console.log('✅ Editing mode enabled for match:', matchId);
      
      // Verify the update
      const { data: verifyData, error: verifyError } = await supabase
        .from('matches')
        .select('status')
        .eq('id', matchId)
        .single();
        
      if (verifyError) {
        console.error('Error verifying editing mode:', verifyError);
      } else {
        console.log('✅ Verified match status:', verifyData?.status);
      }
    } catch (error) {
      console.error('Exception enabling editing mode:', error);
      throw error;
    }
  }

  /**
   * Disable editing mode and return to full-time
   */
  static async disableEditingMode(matchId: string): Promise<void> {
    try {
      // Update match status back to full_time in the matches table
      const { error } = await supabase
        .from('matches')
        .update({ status: 'full_time' })
        .eq('id', matchId);

      if (error) {
        console.error('Error disabling editing mode:', error);
        throw error;
      }

      console.log('✅ Editing mode disabled for match:', matchId);
    } catch (error) {
      console.error('Exception disabling editing mode:', error);
      throw error;
    }
  }
}
